import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {ConfigurationService} from '../../core/services/configuration.service';
import {Parent} from '../../core/shared/model/Parent';
import {catchError, tap} from "rxjs/operators";
import {HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {RefreshDate} from '../../core/shared/model/RefreshDate';
import {ReportParams} from '../../core/shared/model/ReportParams';
import {StoreInvoice} from '../../core/shared/model/StoreInvoice';
import {ErrorService} from '../../core/services/error.service';
import {MessageService} from '../../core/services/message.service';
import {SelectAllCheckboxState} from "@progress/kendo-angular-grid";
import {BaseService} from "~/app/core/services/base.service";


@Injectable({
  providedIn: 'root'
})
export class StatementsService {
  readonly ASOFDATEURL = '/statement/asofdate';
  readonly PARENTSURL = '/users/parents';
  readonly STATEMENTSTORESURL = '/statement/storesummary';
  readonly STATEMENTINVOICESURL = '/statement/invoicesummary';
  readonly STATEMENTINVOICESBILLTOURL = '/statement/invoicesummarybillto';
  readonly STATEMENTREPORTURL = '/statement/statementreport';
  readonly STATEMENTREPORTPARENTURL = '/statement/statementreportparent';
  reportParams: Observable<ReportParams[]>;
  selectAllStateParent: SelectAllCheckboxState = 'unchecked';
  selectAllStateStore: SelectAllCheckboxState = 'unchecked';
  masterParentSelect: boolean = false;
  masterStoreSelect: boolean = false;
  masterInvoiceSelect: boolean = false;

  constructor(private baseService: BaseService,
              private configuration: ConfigurationService,
              private errorService: ErrorService,
              private messageService: MessageService) {
  }

  setSelectAllStateParent(selectedObj: SelectAllCheckboxState) {
    this.selectAllStateParent = selectedObj;
  }

  getSelectAllStateParent(): SelectAllCheckboxState {
    return this.selectAllStateParent;
  }

  setSelectAllStateStore(selectedObj: SelectAllCheckboxState) {
    this.selectAllStateStore = selectedObj;
  }

  getSelectAllStateStore(): SelectAllCheckboxState {
    return this.selectAllStateStore;
  }

  setMasterParentSelect(checked: boolean) {
    this.masterParentSelect = checked;
  }

  getMasterParentSelect(): boolean {
    return this.masterParentSelect;
  }

  setMasterStoreSelect(checked: boolean) {
    this.masterStoreSelect = checked;
  }

  getMasterStoreSelect(): boolean {
    return this.masterStoreSelect;
  }

  setMasterInvoiceSelect(checked: boolean) {
    this.masterInvoiceSelect = checked;
  }

  getMasterInvoiceSelect(): boolean {
    return this.masterInvoiceSelect;
  }

  getAsOfDate(reqType): Observable<RefreshDate> {
    return this.baseService.getHttpObject<RefreshDate>(
      'getAsOfDate',
      'statements-service',
      this.configuration.RESTURL + this.ASOFDATEURL + "/" + reqType).pipe(
      catchError(this.errorService.handleError<RefreshDate>('StatementsService getAsOfDate', reqType))
    );
  }

  getParents(userName, reqType, dateType, fromDate, toDate): Observable<Parent[]> {
    return this.baseService.getHttpObject<Parent[]>(
      'getParents',
      'statements-service',
      this.configuration.RESTURL + this.PARENTSURL + "/" + userName + "/" + dateType + "/" + reqType + "/" + fromDate + "/" + toDate)
      .pipe(
        catchError(this.errorService.handleError('StatementsService getParents', []))
      );
  }


  getStoreSummary(parent, reqType, dateType, fromDate, toDate): Observable<StoreInvoice[]> {
    const data = {
      'billTo': parent.toString(),
      'dateType': dateType,
      'requestType': reqType,
      'fromDate': fromDate,
      'toDate': toDate,
    }

    return this.baseService.postHttpJsonObject<StoreInvoice[]>(
      'getStoreSummary',
      'statements-service',
      this.configuration.RESTURL + this.STATEMENTSTORESURL, JSON.stringify(data))
      .pipe(
        tap(res => {
          console.log(res);
        }),
        catchError((error: any, sub$) => {
          if (error instanceof HttpErrorResponse) {
            if (error.status === 406) {
              return of([]);
            }
          }
          console.log(error);
          return this.errorService.handleError('StatementsService getStoreSummary', [])(error);
        })
      );
  }

  getInvoiceSummary(store, reqType, dateType, fromDate, toDate): Observable<StoreInvoice[]> {
    const data = {
      'shipTo': store.toString(),
      'dateType': dateType,
      'requestType': reqType,
      'fromDate': fromDate,
      'toDate': toDate,
    }

    return this.baseService.postHttpJsonObject<StoreInvoice[]>(
      'getInvoiceSummary',
      'statements-service',
      this.configuration.RESTURL + this.STATEMENTINVOICESURL, JSON.stringify(data)).pipe(
      catchError(this.errorService.handleError('StatementsService getInvoiceSummary', []))
    );
  }

  getInvoiceSummaryBillto(parent, reqType, dateType, fromDate, toDate): Observable<StoreInvoice[]> {
    const data = {
      'billTo': parent.toString(),
      'dateType': dateType,
      'requestType': reqType,
      'fromDate': fromDate,
      'toDate': toDate,
    }

    return this.baseService.postHttpJsonObject<StoreInvoice[]>(
      'getInvoiceSummaryBillto',
      'statements-service',
      this.configuration.RESTURL + this.STATEMENTINVOICESBILLTOURL, JSON.stringify(data)).pipe(
      catchError(this.errorService.handleError('StatementsService getInvoiceSummaryBillto', []))
    );
  }


  getStatementReport(store, reqType, dateType, fromDate, toDate, reportId): Observable<ReportParams[]> {
    const data = {
      'shipTo': store.toString(),
      'dateType': dateType,
      'requestType': reqType,
      'fromDate': fromDate,
      'toDate': toDate,
      'reportId': reportId
    }

    return this.baseService.postHttpJsonObject<ReportParams[]>(
      'getStatementReport',
      'statements-service',
      this.configuration.RESTURL + this.STATEMENTREPORTURL, JSON.stringify(data)).pipe(
      catchError(this.errorService.handleError('StatementsService getStatementReport', []))
    );
  }


  getStatementReportByParent(parents, reqType, dateType, fromDate, toDate, reportId): Observable<ReportParams[]> {
    const data = {
      'billTo': parents.toString(),
      'dateType': dateType,
      'requestType': reqType,
      'fromDate': fromDate,
      'toDate': toDate,
    }

    return this.baseService.postHttpJsonObject<ReportParams[]>(
      'getStatementReportByParent',
      'statements-service',
      this.configuration.RESTURL + this.STATEMENTREPORTPARENTURL, JSON.stringify(data)).pipe(
      catchError(this.errorService.handleError('StatementsService getStatementReport', []))
    );
  }


  submitReport() {
    const headers = new HttpHeaders(this.headers());
    const options: any = {headers, withCredentials: true};
    const body = JSON.stringify('');

    return this.baseService.postHttp('google.com', body, options);

  }

  getPDF() {
    const url = '${this.serviceURL}/pdf';

    const httpOptions = {
      headers: new HttpHeaders(
        {
          'Accept': 'application/json',
          'responseType': 'blob'
        }
      )
    };
    return this.baseService.getHttpObjectOptions<any>(
      'getPDF',
      'statements-service',
      url,
      httpOptions);
  }

  headers(): {} {
    return {
      'Accept': 'application/json',
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      'Content-Type': 'application/json',
      'Expires': 'Sat, 01 Jan 2000 00:00:00 GMT'
    };
  }

  /** Log a service message with the MessageService */
  private log(message: string) {
    this.messageService.add(`CreateGroupService: ${message}`);
  }
}
